/**
 * MapContext - Shared state for Mapbox background map
 *
 * Allows pages (especially GroupedBrowsePage) to control map behavior
 * without directly rendering the map component.
 *
 * The actual MapboxBackground component is rendered in RootLayout
 * and consumes this context for its configuration.
 */

import type { ApiTitle } from "@lumi/data";
import type mapboxgl from "mapbox-gl";
import React from "react";
import type { ReactElement } from "react";

interface MapContextValue {
  /** Map of country names to title counts (for country glow visualization) */
  countryCounts?: Map<string, number>;

  /** Currently selected country (for highlighting) */
  selectedCountry?: string | null;

  /** Currently selected title ID (for marker highlighting/filtering) */
  selectedTitleId?: string | null;

  /** Callback when user clicks a country (null to reset filter) */
  onCountryClick?: (countryName: string | null) => void;

  /** Callback when user clicks an individual title marker (null to reset filter) */
  onTitleClick?: (titleId: string | null) => void;

  /** Blur intensity for the map (in pixels, 0 = no blur) */
  blurAmount?: number;

  /** Filtered titles for marker visualization */
  markerTitles?: ApiTitle[];
  /** Human-readable summary of the active filters (used for cluster tooltips) */
  markerFilterSummary?: string;

  /** Mapbox GL map instance (set by MapboxBackground, consumed by pages) */
  map?: mapboxgl.Map | null;

  /** Whether the map has finished loading */
  isMapLoaded?: boolean;

  /** Update the map context values */
  setMapContext: (value: Partial<Omit<MapContextValue, "setMapContext">>) => void;
}

const MapContext = React.createContext<MapContextValue>({
  setMapContext: () => {
    // Default no-op
  },
});

/**
 * Provider component for map state
 *
 * Usage:
 * ```tsx
 * <MapProvider>
 *   <YourApp />
 * </MapProvider>
 * ```
 *
 * Then in any child component:
 * ```tsx
 * const { setMapContext } = useMapContext();
 * setMapContext({ countryCounts, selectedCountry, onCountryClick });
 * ```
 */
export function MapProvider({ children }: { readonly children: React.ReactNode }): ReactElement {
  const [mapState, setMapState] = React.useState<Omit<MapContextValue, "setMapContext">>({});

  const setMapContext = React.useCallback((value: Partial<Omit<MapContextValue, "setMapContext">>) => {
    setMapState((prev) => ({ ...prev, ...value }));
  }, []);

  const contextValue = React.useMemo(
    () => ({
      ...mapState,
      setMapContext,
    }),
    [mapState, setMapContext],
  );

  return <MapContext.Provider value={contextValue}>{children}</MapContext.Provider>;
}

/**
 * Hook to access map state from any component
 *
 * Usage:
 * ```tsx
 * // To consume map state (in RootLayout):
 * const { countryCounts, selectedCountry, onCountryClick } = useMapContext();
 *
 * // To provide map state (in GroupedBrowsePage):
 * const { setMapContext } = useMapContext();
 * setMapContext({ countryCounts, selectedCountry, onCountryClick });
 * ```
 */
export function useMapContext(): MapContextValue {
  return React.useContext(MapContext);
}
